/*
 * Ryan O'Dowd
 * 2026-01-10
 * © Copyright 2026 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
// from chatgpt
import {
  Alert,
  // @TODO: Linking,
  Platform,
} from 'react-native';
import {
  RESULTS,
  checkNotifications,
  requestNotifications,
} from 'react-native-permissions';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  setDontAskForPNPerms,
} from '@utils/store/actions';
import useTypedDispatch from './useTypedDispatch';
import useTypedSelector from './useTypedSelector';

type NotificationStatus = 'unavailable' | 'denied' | 'blocked' | 'granted' | 'limited';

export default function useRequestNotificationPermission(rationaleMessage) {
  const dispatch = useTypedDispatch();

  const dontAskForPNPerms = useTypedSelector((state) => state.dontAskForPNPerms);
  const lifetimePeanuts = useTypedSelector((state) => state.oakUser.lifetime_peanuts);

  const [status, setStatus] = useState<NotificationStatus>('unavailable');

  const checkPermission = useCallback(async () => {
    const {status: currentStatus} = await checkNotifications();
    setStatus(currentStatus);
    return currentStatus;
  }, []);

  const requestPermission = useCallback(async () => {
    const currentStatus = await checkPermission();

    if (lifetimePeanuts === 0) {
      // don't want this dialog/alert to interfere with walkthrough tutorial on first run
      return currentStatus;
    }

    // Only request if not already granted and if not previously soft denied
    if (dontAskForPNPerms || (currentStatus === RESULTS.GRANTED || currentStatus === RESULTS.LIMITED)) {
      return currentStatus;
    }

    // Show rationale alert on Android 13+
    // @TODO: show on ios as well...right now, the os alert shows before this one, so not showing it right now
    if ((Platform.OS === 'android' && Platform.Version >= 33) && rationaleMessage) {
      let proceed = false;

      await new Promise<void>((resolve) => {
        Alert.alert(
          'Push notifications',
          rationaleMessage,
          [
            {text: "Don't allow", style: 'cancel', onPress: () => {
              dispatch(setDontAskForPNPerms(true));
              resolve();
            }},
            {text: 'Allow', onPress: () => {
              proceed = true;
              resolve();
            }},
          ],
          {cancelable: true},
        );
      });

      if (!proceed) {
        return currentStatus;
      }
    }

    // Request notification permission
    const {status: newStatus} = await requestNotifications(['alert', 'sound', 'badge']);
    setStatus(newStatus);

    /* @TODO: this should be used only in very specific scenarios (e.g. when enabling passage of the day, not on app open)
    // If denied, suggest opening settings
    if (newStatus === RESULTS.BLOCKED) {
      Alert.alert(
        'Notifications Blocked',
        'Please enable notifications in your device settings.',
        [
          {text: 'Cancel', style: 'cancel'},
          {text: 'Open Settings', onPress: () => Linking.openSettings()},
        ],
        {cancelable: true},
      );
    }
    */

    return newStatus;
  }, [rationaleMessage, checkPermission, dispatch, dontAskForPNPerms, lifetimePeanuts]);

  // Automatically check and request permission on mount
  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  return status;
}
